// EditPost.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, TextField, MenuItem, Box } from "@mui/material";
import "./EditPost.css"; // We'll create this CSS file

const EditPost = () => {
  const { id } = useParams(); // Get the post ID from the URL
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form states
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [category, setCategory] = useState("Announcements");
  const [content, setContent] = useState("");
  const [imageUrls, setImageUrls] = useState([]); // Assuming image URLs are not directly editable here without a separate file upload logic

  const categories = ["Announcements", "News/Events", "Admissions", "Other"];

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/posts/${id}`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setPost(data);
        // Pre-populate form fields with fetched data
        setTitle(data.title || "");
        setSubtitle(data.subtitle || "");
        setCategory(data.category || "Announcements");
        setContent(data.content || "");
        setImageUrls(data.imageUrls || []); // Set existing image URLs
      } catch (err) {
        setError("Failed to fetch post for editing. Please try again later.");
        console.error("Error fetching single post for edit:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const updatedPost = {
      title,
      subtitle,
      category,
      content,
      // You might need to handle imageUrls if you allow changing them on edit screen
      // For simplicity, we are keeping existing ones. If you need to upload new,
      // you'd integrate file upload logic here similar to your create post screen.
      imageUrls: post.imageUrls, // Assuming you don't allow changing images on this edit screen
    };

    try {
      console.log("PUT URL:", `${process.env.REACT_APP_API_URL}/posts/${id}`);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/posts/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedPost),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      navigate(`/posts/${id}`); // Navigate back to the single post view
    } catch (err) {
      setError(`Failed to update post: ${err.message}`);
      console.error("Error updating post:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="edit-post-container">Loading post for edit...</div>;
  }

  if (error && !post) {
    // Only show error if post data couldn't be loaded at all
    return <div className="edit-post-container error">{error}</div>;
  }

  if (!post) {
    return (
      <div className="edit-post-container">
        No post found with this ID for editing.
      </div>
    );
  }

  return (
    <div className="edit-post-container">
      <h1>Edit Post</h1>
      {error && <div className="error-message">{error}</div>}
      <form onSubmit={handleSubmit} className="edit-post-form">
        <TextField
          label="Title"
          variant="outlined"
          fullWidth
          margin="normal"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />
        <TextField
          label="Subtitle"
          variant="outlined"
          fullWidth
          margin="normal"
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          required
        />
        <TextField
          select
          label="Category"
          variant="outlined"
          fullWidth
          margin="normal"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          required
        >
          {categories.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Content"
          variant="outlined"
          fullWidth
          margin="normal"
          multiline
          rows={10}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          required
        />

        <Button
          type="submit"
          variant="contained"
          color="primary"
          sx={{ mt: 3 }}
          disabled={loading}
        >
          {loading ? "Updating..." : "Update Post"}
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          sx={{ mt: 3, ml: 2 }}
          onClick={() => navigate(`/posts/${id}`)}
          disabled={loading}
        >
          Cancel
        </Button>
      </form>
    </div>
  );
};

export default EditPost;
